/**
 * analytics.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Client-side Google Analytics 4 event helpers.
 *
 * Uses react-ga4 (initialized in index.js via ReactGA.initialize).
 * No manual script tag in index.html required.
 *
 * Wire-up summary:
 *   pageView    — App.jsx useEffect on location.pathname
 *   viewItem    — RestaurantProductDialog, Product.jsx on open
 *   addToCart   — all handleAddItem handlers
 *   beginCheckout — all ShippingScreen components on mount
 *   purchase    — all PlaceOrderScreen on success
 *   search      — SearchResultsPage after results load
 */

import ReactGA from 'react-ga4';
import { CURRENCY } from '../config/market';

// ── Core fire helper ──────────────────────────────────────────────────────────

function fire(eventName, params) {
  try {
    ReactGA.event(eventName, params);
  } catch (_) {}
}

// ── Item builder ─────────────────────────────────────────────────────────────

/**
 * Build a GA4-compatible item object.
 *
 * Accepts EITHER:
 *   buildItem({ id, name, price, vendorName, category, quantity })  — flat form
 *   buildItem(product, vendor, qty)                                  — legacy form
 *
 * @param {object} productOrFlat
 * @param {object} [vendor]
 * @param {number} [qty=1]
 */
function buildItem(productOrFlat, vendor, qty = 1) {
  const p = productOrFlat;
  // Flat-object form: { id, name, price, vendorName, ... }
  if (p.id !== undefined || p.vendorName !== undefined) {
    return {
      item_id:       String(p.id || p._id || ''),
      item_name:     p.name       || '',
      item_brand:    p.vendorName || 'Lusuku',
      item_category: p.category   || p.vendorType || 'General',
      price:         p.price      || 0,
      quantity:      p.quantity   || qty,
      currency:      CURRENCY,
    };
  }
  // Legacy form: (product, vendor, qty)
  return {
    item_id:       String(p._id || p.productId || ''),
    item_name:     p.name       || '',
    item_brand:    vendor?.name || vendor?.tradingName || 'Lusuku',
    item_category: p.category   || p.vendorType || 'General',
    price:         p.price      || 0,
    quantity:      qty,
    currency:      CURRENCY,
  };
}

// ── Exported event functions ──────────────────────────────────────────────────

/**
 * Track a page view — call on every route change.
 * @param {string} path  e.g. '/restaurant/shop/abc'
 */
export const pageView = (path) => {
  try {
    ReactGA.send({ hitType: 'pageview', page: path });
  } catch (_) {}
};

/**
 * Track a vendor/shop view with a stable, URL-independent identity.
 *
 * This is the durable replacement for path-based vendor attribution: it emits
 * a `vendor_view` event carrying `vendor_id` (the vendor's Mongo id) and
 * `vendor_type`, so backend analytics can attribute traffic to a shop without
 * parsing the URL slug or relying on the (race-prone) page title. Register
 * `vendor_id` and `vendor_type` as event-scoped GA4 custom dimensions to use
 * them in reports.
 *
 * Fire this once per shop view, AFTER the vendor record has loaded (so the id
 * is known). Pass the vendor-model `_id` when available; the backend also
 * tolerates the owner id.
 *
 * @param {object} args
 * @param {string} args.vendorId    - Vendor Mongo id (model _id preferred)
 * @param {string} [args.vendorType]- 'restaurant' | 'healthcare' | 'retailer' | 'wholesaler' | 'brand'
 * @param {string} [args.path]      - Page path; defaults to current pathname
 */
export const vendorView = ({ vendorId, vendorType, path } = {}) => {
  if (!vendorId) return;
  fire('vendor_view', {
    vendor_id:   String(vendorId),
    vendor_type: vendorType || 'unknown',
    page_path:   path || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });
};

/**
 * Track a product detail view.
 * Accepts flat object { id, name, price, vendorName } OR (product, vendor).
 * @param {object} productOrFlat
 * @param {object} [vendor]
 */
export const viewItem = (productOrFlat, vendor) => {
  const item = buildItem(productOrFlat, vendor);
  fire('view_item', {
    currency: CURRENCY,
    value:    item.price,
    items:    [item],
  });
};

/**
 * Track an add-to-cart event.
 * Accepts flat object { id, name, price, quantity, vendorName } OR (product, vendor, qty).
 * @param {object} productOrFlat
 * @param {object} [vendor]
 * @param {number} [qty=1]
 */
export const addToCart = (productOrFlat, vendor, qty = 1) => {
  const item = buildItem(productOrFlat, vendor, qty);
  fire('add_to_cart', {
    currency: CURRENCY,
    value:    item.price * item.quantity,
    items:    [item],
  });
};

/**
 * Track checkout initiation (ShippingScreen mount).
 * @param {Array<{product, vendor, qty}>} cartItems
 * @param {number} total
 */
export const beginCheckout = (cartItems = [], total = 0) =>
  fire('begin_checkout', {
    currency: CURRENCY,
    value:    total,
    items:    cartItems.map(({ product, vendor, qty = 1 }) =>
                buildItem(product, vendor, qty)),
  });

/**
 * Track a completed purchase (PlaceOrderScreen on success).
 * @param {string} orderId
 * @param {Array<{product, vendor, qty}>} cartItems
 * @param {number} total
 */
export const purchase = (orderId, cartItems = [], total = 0) =>
  fire('purchase', {
    transaction_id: String(orderId),
    currency:       CURRENCY,
    value:          total,
    items:          cartItems.map(({ product, vendor, qty = 1 }) =>
                      buildItem(product, vendor, qty)),
  });

/**
 * Track a search query.
 * Accepts flat object { searchTerm, resultCount } OR (query, resultCount).
 * @param {string|object} queryOrFlat
 * @param {number} [resultCount=0]
 */
export const search = (queryOrFlat, resultCount = 0) => {
  const term  = typeof queryOrFlat === 'object'
    ? queryOrFlat.searchTerm  || ''
    : queryOrFlat;
  const count = typeof queryOrFlat === 'object'
    ? queryOrFlat.resultCount ?? resultCount
    : resultCount;
  fire('search', { search_term: term, result_count: count });
};
